import {
    Box,
    Button,
    ChakraProps,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { useTranslation } from 'helpers/languages/translations';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { linkStyle } from 'shared/styles';
import { TGlobalCategoriesHooks } from '../../useGlobalCategories';
import useModal from './useModal';

import { Icon } from '@chakra-ui/react';
import GenericFilter from 'plugins/DITable/filters/GenericFiler';
interface IProps {
    dataToSubmit: TGlobalCategoriesHooks['dataToSubmit'];
    setDataToSubmit: TGlobalCategoriesHooks['setDataToSubmit'];
    availableCategories: TGlobalCategoriesHooks['availableCategories'];
    setAvailableCategories: TGlobalCategoriesHooks['setAvailableCategories'];
    setIsModalOpen: TGlobalCategoriesHooks['setIsModalOpen'];
    isModalOpen: TGlobalCategoriesHooks['isModalOpen'];
    modalType: TGlobalCategoriesHooks['modalType'];
    deleteGlobalCategory: TGlobalCategoriesHooks['deleteGlobalCategory'];
    isDeleteLoading: TGlobalCategoriesHooks['isDeleteLoading'];
}

const globalCategoriesModal = ({
    dataToSubmit,
    setDataToSubmit,
    availableCategories,
    deleteGlobalCategory,
    isDeleteLoading,
    setAvailableCategories,
    setIsModalOpen,
    isModalOpen,
    modalType,
}: IProps) => {
    const {
        isAddGlobalCategoryLoading,
        isUpdateGlobalCategoryLoading,
        handleDeleteGlobalCategory,
        handleCloseModal,
        handleSubmit,
        generateCategoryItemProps,
        moveCategories,
        handleSelectCategory,
        handleSelectAllCategories,
        handleCategoryNameChange,
        updateInitialRef,
        filteredAvailableCategories,
        filteredToSubmitCategories,
        handleSearchChange,
        handleToSubmitSearchChange,
        searchValue,
        toSubmitSearchValue,
        isSubmitDisabled,
    } = useModal({
        dataToSubmit,
        setDataToSubmit,
        setIsModalOpen,
        modalType,
        availableCategories,
        setAvailableCategories,
        deleteGlobalCategory,
        isDeleteLoading,
    });

    const translate = useTranslation();
    const categoriesListBoxStyles: Omit<ChakraProps, never> = {
        height: '100%',
        mt: '10px',
        border: '1px solid',
        borderColor: 'borderGray',
        flexDir: 'column',
        width: '100%',
        overflowY: 'scroll',
    };
    const inputStyles: Omit<ChakraProps, never> = {
        mb: '8px',
        fontSize: 'sm',
        fontWeight: 'bold',
    };

    const modalTitle =
        modalType === 'add'
            ? translate('global_categories.modal.title.add')
            : translate('global_categories.modal.title.update');

    return (
        <Modal
            initialFocusRef={updateInitialRef}
            size="6xl"
            isOpen={isModalOpen}
            onClose={() => {
                handleCloseModal();
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader tabIndex={-1} ref={updateInitialRef}>
                    {modalTitle}
                </ModalHeader>
                <ModalCloseButton
                    mt="2"
                    isDisabled={modalType === 'add' ? isAddGlobalCategoryLoading : isUpdateGlobalCategoryLoading}
                />

                <ModalBody>
                    <Flex flex="1" gap="15px" mb="16px">
                        <Flex flex="1" flexDir="column">
                            <Text {...inputStyles}>{translate('global_categories.modal.field.category')}</Text>
                            <Input
                                placeholder={translate('placeholder.search') as string}
                                value={dataToSubmit.level_name_1}
                                onChange={(e) => handleCategoryNameChange(e.target.value, 'level_name_1')}
                            />
                        </Flex>
                        <Flex flex="1" flexDir="column">
                            <Text {...inputStyles}>{translate('global_categories.modal.field.subcategory')}</Text>
                            <Input
                                placeholder={translate('placeholder.search') as string}
                                value={dataToSubmit.level_name_2}
                                onChange={(e) => handleCategoryNameChange(e.target.value, 'level_name_2')}
                            />
                        </Flex>
                    </Flex>

                    <Flex flex="1" mb="16px">
                        <Flex flex="1" flexDir="column">
                            <Text {...inputStyles}>{translate('global_categories.modal.field.segment')}</Text>
                            <Input
                                placeholder={translate('placeholder.search') as string}
                                value={dataToSubmit.level_name_3}
                                onChange={(e) => handleCategoryNameChange(e.target.value, 'level_name_3')}
                            />
                        </Flex>
                    </Flex>

                    <Flex flex="1" mb="16px" mt="25px" gap="15px" h="400px">
                        <Flex flex="1" mb="16px" flexDir="column">
                            <Flex w="100%" justifyContent="space-between" alignItems="center">
                                <Text as="span" fontSize="sm" fontWeight="bold">
                                    {translate('global_categories.modal.field.available_categories')}
                                </Text>

                                <Box>
                                    <Text
                                        {...linkStyle}
                                        as="span"
                                        fontSize="sm"
                                        onClick={() => handleSelectAllCategories('available', 'select')}
                                    >
                                        {translate('global_categories.modal.select_all')}
                                    </Text>
                                    /
                                    <Text
                                        {...linkStyle}
                                        as="span"
                                        fontSize="sm"
                                        onClick={() => handleSelectAllCategories('available', 'deselect')}
                                    >
                                        {translate('global_categories.modal.deselect_all')}
                                    </Text>
                                </Box>
                            </Flex>
                            <GenericFilter
                                value={searchValue}
                                onChange={handleSearchChange}
                                inputGroupStyles={{ width: '100%', marginTop: '10px' }}
                            />
                            <Box {...categoriesListBoxStyles}>
                                {filteredAvailableCategories?.map((category) => (
                                    <Flex
                                        {...generateCategoryItemProps(category)}
                                        key={category.id}
                                        onClick={() => handleSelectCategory(category, 'available')}
                                    >
                                        {`${category.company_name} - ${category.level_name_1} - ${category.level_name_2} - ${category.level_name_3}`}
                                    </Flex>
                                ))}
                            </Box>
                        </Flex>
                        <Flex mb="16px" minH="100%" flexDir="column" justifyContent="center" alignItems="center">
                            <Box
                                borderRadius="50%"
                                border="1px solid"
                                borderColor="borderGray"
                                p="10px"
                                mb="10px"
                                cursor="pointer"
                                onClick={() => moveCategories('right')}
                            >
                                <Icon viewBox="0 0 24 24" boxSize={5}>
                                    <path
                                        fill="currentColor"
                                        d="M12 2L10.59 3.41 18.17 11H2v2h16.17l-7.58 7.59L12 22l10-10z"
                                    />
                                </Icon>
                            </Box>

                            <Box
                                borderRadius="50%"
                                border="1px solid"
                                borderColor="borderGray"
                                p="10px"
                                mt="10px"
                                cursor="pointer"
                                onClick={() => moveCategories('left')}
                            >
                                <Icon viewBox="0 0 24 24" boxSize={5}>
                                    <path
                                        fill="currentColor"
                                        d="M12 22l1.41-1.41L5.83 13H22v-2H5.83l7.58-7.59L12 2l-10 10z"
                                    />
                                </Icon>
                            </Box>
                        </Flex>
                        <Flex flex="1" mb="16px" flexDir="column">
                            <Flex w="100%" justifyContent="space-between" alignItems="center">
                                <Text as="span" fontSize="sm" fontWeight="bold">
                                    {translate('global_categories.modal.field.categories_to_submit')}
                                </Text>

                                <Box>
                                    <Text
                                        {...linkStyle}
                                        as="span"
                                        fontSize="sm"
                                        onClick={() => handleSelectAllCategories('to_submit', 'select')}
                                    >
                                        {translate('global_categories.modal.select_all')}
                                    </Text>
                                    /
                                    <Text
                                        {...linkStyle}
                                        as="span"
                                        fontSize="sm"
                                        onClick={() => handleSelectAllCategories('to_submit', 'deselect')}
                                    >
                                        {translate('global_categories.modal.deselect_all')}
                                    </Text>
                                </Box>
                            </Flex>
                            <GenericFilter
                                value={toSubmitSearchValue}
                                onChange={handleToSubmitSearchChange}
                                inputGroupStyles={{ width: '100%', marginTop: '10px' }}
                            />
                            <Box {...categoriesListBoxStyles} flexDir="column" width="100%">
                                {filteredToSubmitCategories?.map((category) => (
                                    <Flex
                                        {...generateCategoryItemProps(category)}
                                        key={category.id}
                                        onClick={() => handleSelectCategory(category, 'to_submit')}
                                    >
                                        {`${category.company_name} - ${category.level_name_1} - ${category.level_name_2} - ${category.level_name_3}`}
                                    </Flex>
                                ))}
                            </Box>
                        </Flex>
                    </Flex>
                </ModalBody>
                <ModalFooter fontSize="sm" justifyContent="space-between">
                    {modalType === 'update' && (
                        <Box
                            {...linkStyle}
                            color="red.500"
                            onClick={intentionalyFloatingPromiseReturn(handleDeleteGlobalCategory)}
                        >
                            {translate('global_categories.modal.button.delete')}
                        </Box>
                    )}

                    <Flex flex="1" justifyContent="flex-end">
                        <Button
                            isDisabled={
                                modalType === 'add' ? isAddGlobalCategoryLoading : isUpdateGlobalCategoryLoading
                            }
                            size="sm"
                            variant="outline"
                            mr={3}
                            onClick={handleCloseModal}
                        >
                            {translate('global_categories.modal.button.cancel')}
                        </Button>
                        <Button
                            onClick={intentionalyFloatingPromiseReturn(handleSubmit)}
                            isLoading={modalType === 'add' ? isAddGlobalCategoryLoading : isUpdateGlobalCategoryLoading}
                            isDisabled={isSubmitDisabled}
                            size="sm"
                            colorScheme="blue"
                            bg="blueMain"
                        >
                            {translate('global_categories.modal.button.confirm')}
                        </Button>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default globalCategoriesModal;
